import { formatDuration, formatWhen } from '../format.ts';
import type { SopPdfInput } from './downloadSopPdf.ts';
import { parseSopMarkdown } from './parseSopMarkdown.ts';
import type { SopMarkupAnnotation } from '../../../request/sop-synthesizer/sopSynthesizer.api.ts';

type LoadedFrame = { dataUrl: string; width: number; height: number };

function safeFilename(title: string): string {
  const base = title.trim().replace(/[^\w\s-]+/g, '').replace(/\s+/g, '-').slice(0, 80);
  return `${base || 'sop'}.doc`;
}

function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function strokeColor(color: string | undefined): string {
  const raw = (color || '#ef4444').trim();
  return /^#?[0-9a-fA-F]{6}$/.test(raw) ? (raw.startsWith('#') ? raw : `#${raw}`) : '#ef4444';
}

function splitHighlightSegments(text: string, quotes: string[]): { text: string; highlight: boolean }[] {
  const unique = [...new Set(quotes.map((q) => q.trim()).filter((q) => q.length >= 2))].sort(
    (a, b) => b.length - a.length,
  );
  if (!unique.length || !text) return [{ text, highlight: false }];

  type Hit = { start: number; end: number };
  const hits: Hit[] = [];
  const lower = text.toLowerCase();
  for (const q of unique) {
    const needle = q.toLowerCase();
    let from = 0;
    while (from < text.length) {
      const idx = lower.indexOf(needle, from);
      if (idx === -1) break;
      const end = idx + q.length;
      const overlaps = hits.some((h) => idx < h.end && end > h.start);
      if (!overlaps) hits.push({ start: idx, end });
      from = idx + 1;
    }
  }
  hits.sort((a, b) => a.start - b.start);
  if (!hits.length) return [{ text, highlight: false }];

  const out: { text: string; highlight: boolean }[] = [];
  let cursor = 0;
  for (const h of hits) {
    if (h.start > cursor) out.push({ text: text.slice(cursor, h.start), highlight: false });
    out.push({ text: text.slice(h.start, h.end), highlight: true });
    cursor = h.end;
  }
  if (cursor < text.length) out.push({ text: text.slice(cursor), highlight: false });
  return out;
}

function htmlHighlighted(text: string, highlightQuotes: string[]): string {
  return splitHighlightSegments(text, highlightQuotes)
    .map((seg) =>
      seg.highlight
        ? `<span style="background:#facc15">${esc(seg.text)}</span>`
        : esc(seg.text),
    )
    .join('');
}

async function loadFrame(url: string): Promise<LoadedFrame | null> {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    try {
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const el = new Image();
        el.onload = () => resolve(el);
        el.onerror = () => reject(new Error('image load failed'));
        el.src = objectUrl;
      });
      const width = img.naturalWidth || img.width;
      const height = img.naturalHeight || img.height;
      if (!width || !height) return null;
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0);
      return { dataUrl: canvas.toDataURL('image/jpeg', 0.92), width, height };
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  } catch {
    return null;
  }
}

/** Draw image markups onto the screenshot so Word gets a single embedded picture. */
async function bakeMarksOntoFrame(frame: LoadedFrame, marks: SopMarkupAnnotation[]): Promise<string> {
  if (!marks.length) return frame.dataUrl;
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = () => reject(new Error('image load failed'));
    el.src = frame.dataUrl;
  });
  const w = frame.width;
  const h = frame.height;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return frame.dataUrl;
  ctx.drawImage(img, 0, 0);
  ctx.lineWidth = Math.max(2, Math.min(w, h) * 0.004);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  for (const a of marks) {
    const c = strokeColor(a.color);
    ctx.strokeStyle = c;
    ctx.fillStyle = c;
    if ((a.type === 'image_rect' || a.type === 'image_ellipse') && a.x != null && a.y != null && a.w != null && a.h != null) {
      const x = a.x * w;
      const y = a.y * h;
      const rw = a.w * w;
      const rh = a.h * h;
      ctx.beginPath();
      if (a.type === 'image_rect') ctx.rect(x, y, rw, rh);
      else ctx.ellipse(x + rw / 2, y + rh / 2, Math.max(1, rw / 2), Math.max(1, rh / 2), 0, 0, Math.PI * 2);
      ctx.fillStyle = `${c}22`;
      ctx.fill();
      ctx.strokeStyle = c;
      ctx.stroke();
    } else if (a.type === 'image_arrow' && a.x1 != null && a.y1 != null && a.x2 != null && a.y2 != null) {
      const x1 = a.x1 * w;
      const y1 = a.y1 * h;
      const x2 = a.x2 * w;
      const y2 = a.y2 * h;
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
      const ang = Math.atan2(y2 - y1, x2 - x1);
      const ah = Math.max(8, Math.min(w, h) * 0.02);
      ctx.beginPath();
      ctx.moveTo(x2, y2);
      ctx.lineTo(x2 - ah * Math.cos(ang - 0.4), y2 - ah * Math.sin(ang - 0.4));
      ctx.moveTo(x2, y2);
      ctx.lineTo(x2 - ah * Math.cos(ang + 0.4), y2 - ah * Math.sin(ang + 0.4));
      ctx.stroke();
    } else if (a.type === 'image_pen' && Array.isArray(a.points) && a.points.length > 1) {
      ctx.beginPath();
      for (let i = 0; i < a.points.length; i += 1) {
        const p = a.points[i];
        if (!p || p.length < 2) continue;
        const x = p[0] * w;
        const y = p[1] * h;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();
    } else if (a.type === 'image_text' && a.x != null && a.y != null && a.text) {
      const fontSize = Math.max(12, Math.min(w, h) * 0.03);
      ctx.font = `bold ${fontSize}px sans-serif`;
      ctx.fillStyle = '#ffffffcc';
      const label = a.text.slice(0, 60);
      const tw = ctx.measureText(label).width;
      ctx.fillRect(a.x * w, a.y * h, tw + 8, fontSize + 8);
      ctx.fillStyle = c;
      ctx.fillText(label, a.x * w + 4, a.y * h + fontSize + 2);
    } else if (a.type === 'image_callout' && a.x != null && a.y != null) {
      const r = Math.max(10, Math.min(w, h) * 0.025);
      const cx = a.x * w;
      const cy = a.y * h;
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#fff';
      ctx.font = `bold ${Math.max(10, r)}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(String(a.callout_number ?? a.text ?? ''), cx, cy);
      ctx.textAlign = 'start';
      ctx.textBaseline = 'alphabetic';
    } else if (a.type === 'image_pin' && a.x != null && a.y != null) {
      const r = Math.max(7, Math.min(w, h) * 0.018);
      ctx.beginPath();
      ctx.arc(a.x * w, a.y * h, r, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  return canvas.toDataURL('image/jpeg', 0.92);
}

function dataUrlToBase64(dataUrl: string): string {
  const i = dataUrl.indexOf(',');
  return i >= 0 ? dataUrl.slice(i + 1) : dataUrl;
}

function wrapBase64(b64: string, width = 76): string {
  const lines: string[] = [];
  for (let i = 0; i < b64.length; i += width) lines.push(b64.slice(i, i + width));
  return lines.join('\r\n');
}

/**
 * Word often ignores data: URLs in plain HTML .doc files.
 * MHTML (multipart/related + cid:) is what Word expects for embedded pictures.
 */
function buildMhtml(title: string, htmlBody: string, images: { cid: string; base64: string }[]): string {
  const boundary = '----=_NextPart_SopExport_001';
  const rootLoc = 'file:///C:/sop.htm';
  const chunks: string[] = [];
  chunks.push('MIME-Version: 1.0');
  chunks.push(`Content-Type: multipart/related; boundary="${boundary}"`);
  chunks.push('');
  chunks.push(`--${boundary}`);
  chunks.push('Content-Type: text/html; charset="utf-8"');
  chunks.push('Content-Transfer-Encoding: base64');
  chunks.push(`Content-Location: ${rootLoc}`);
  chunks.push('');
  // base64 avoids quoted-printable soft-breaks splitting tags / cid: refs
  const htmlB64 = btoa(unescape(encodeURIComponent(htmlBody)));
  chunks.push(wrapBase64(htmlB64));
  chunks.push('');

  for (const img of images) {
    chunks.push(`--${boundary}`);
    chunks.push('Content-Type: image/jpeg');
    chunks.push('Content-Transfer-Encoding: base64');
    chunks.push(`Content-ID: <${img.cid}>`);
    chunks.push(`Content-Location: file:///C:/${img.cid}.jpg`);
    chunks.push('');
    chunks.push(wrapBase64(img.base64));
    chunks.push('');
  }

  chunks.push(`--${boundary}--`);
  chunks.push('');
  return chunks.join('\r\n');
}

function triggerDownload(filename: string, content: string): void {
  const blob = new Blob([content], { type: 'application/msword;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Build and download an SOP as a Word-openable .doc (MHTML) — no new packages. */
export async function downloadSopDoc(input: SopPdfInput): Promise<void> {
  const { sop, sections, frames, videoTitle, videoDurationSeconds, markupAnnotations = [] } = input;
  if (!sections.length) {
    throw new Error('This SOP has no sections to export yet.');
  }

  const frameIds = new Set<string>();
  for (const section of sections) {
    for (const block of parseSopMarkdown(section.body)) {
      if (block.type === 'image') frameIds.add(block.frameId);
    }
  }

  const imageData = new Map<string, LoadedFrame>();
  await Promise.all(
    [...frameIds].map(async (id) => {
      const url = frames[id];
      if (!url) return;
      const loaded = await loadFrame(url);
      if (loaded) imageData.set(id, loaded);
    }),
  );

  const embeddedImages: { cid: string; base64: string }[] = [];
  let imgSeq = 0;

  const htmlParts: string[] = [];
  htmlParts.push(`<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
<head>
<meta http-equiv="Content-Type" content="text/html; charset=utf-8" />
<title>${esc(sop.title)}</title>
<!--[if gte mso 9]><xml><w:WordDocument><w:View>Print</w:View></w:WordDocument></xml><![endif]-->
<style>
  body { font-family: Calibri, Arial, sans-serif; font-size: 11pt; color: #1f2937; }
  h1 { font-size: 18pt; color: #111827; }
  h2 { font-size: 12pt; color: #fff; background: #1e40af; padding: 6pt 8pt; }
  h3 { font-size: 11pt; background: #f3f4f6; border-left: 3pt solid #1e40af; padding: 4pt 8pt; }
  .meta { color: #6b7280; font-size: 9pt; margin-bottom: 12pt; }
  .desc { color: #4b5563; margin-bottom: 8pt; }
  table { border-collapse: collapse; width: 100%; margin: 8pt 0; }
  th, td { border: 1px solid #d1d5db; padding: 4pt 6pt; text-align: left; vertical-align: top; }
  th { background: #1e40af; color: #fff; }
  /* Word ignores CSS height:auto; size via HTML width/height attrs only. */
  img.frame { margin: 6pt 0; border: 0; }
  .caption { color: #6b7280; font-size: 9pt; margin-bottom: 8pt; }
  .comments-title { color: #1e40af; font-size: 9pt; font-weight: bold; margin-top: 8pt; }
  .comment { background: #eff6ff; border: 1px solid #bae6fd; padding: 6pt 8pt; margin: 4pt 0; }
  .quote { font-style: italic; color: #374151; }
  .unavailable { color: #9ca3af; font-style: italic; }
</style>
</head>
<body>`);

  htmlParts.push(`<h1>${esc(sop.title)}</h1>`);
  if (sop.description?.trim()) {
    htmlParts.push(`<p class="desc">${esc(sop.description.trim())}</p>`);
  }
  const meta = [
    `Version v${sop.row_version}`,
    sop.updated_at ? `Updated ${formatWhen(sop.updated_at)}` : null,
    videoTitle
      ? `Source: ${videoTitle}${videoDurationSeconds ? ` (${formatDuration(videoDurationSeconds)})` : ''}`
      : null,
  ]
    .filter(Boolean)
    .join(' · ');
  htmlParts.push(`<p class="meta">${esc(meta)}</p>`);

  for (let s = 0; s < sections.length; s += 1) {
    const section = sections[s];
    const sectionMarks = markupAnnotations.filter((a) => a.section_id === section.id);
    const highlightQuotes = sectionMarks
      .filter((a) => a.type === 'highlight' && a.quote)
      .map((a) => a.quote as string);
    const sectionComments = sectionMarks.filter((a) => a.type === 'comment');
    const sectionImageMarks = sectionMarks.filter((a) => typeof a.type === 'string' && a.type.startsWith('image_'));

    htmlParts.push(`<h2>${esc(`${s + 1}. ${section.heading}`.toUpperCase())}</h2>`);

    for (const block of parseSopMarkdown(section.body)) {
      if (block.type === 'heading') {
        htmlParts.push(`<h3>${htmlHighlighted(block.text.toUpperCase(), highlightQuotes)}</h3>`);
      } else if (block.type === 'paragraph') {
        htmlParts.push(`<p>${htmlHighlighted(block.text, highlightQuotes)}</p>`);
      } else if (block.type === 'list') {
        const tag = block.ordered ? 'ol' : 'ul';
        htmlParts.push(`<${tag}>`);
        for (const item of block.items) {
          htmlParts.push(`<li>${htmlHighlighted(item, highlightQuotes)}</li>`);
        }
        htmlParts.push(`</${tag}>`);
      } else if (block.type === 'table') {
        htmlParts.push('<table><thead><tr>');
        for (const h of block.headers) htmlParts.push(`<th>${esc(h)}</th>`);
        htmlParts.push('</tr></thead><tbody>');
        for (const row of block.rows) {
          htmlParts.push('<tr>');
          for (const cell of row) htmlParts.push(`<td>${esc(cell)}</td>`);
          htmlParts.push('</tr>');
        }
        htmlParts.push('</tbody></table>');
      } else if (block.type === 'image') {
        const frame = imageData.get(block.frameId);
        if (!frame) {
          htmlParts.push(
            `<p class="unavailable">${esc(
              block.caption ? `Screenshot unavailable: ${block.caption}` : 'Screenshot unavailable',
            )}</p>`,
          );
          continue;
        }
        const marks = sectionImageMarks.filter((m) => m.frame_id === block.frameId);
        let dataUrl = frame.dataUrl;
        try {
          dataUrl = await bakeMarksOntoFrame(frame, marks);
        } catch {
          dataUrl = frame.dataUrl;
        }
        imgSeq += 1;
        const cid = `sopframe${imgSeq}@local`;
        embeddedImages.push({ cid, base64: dataUrlToBase64(dataUrl) });
        // Word needs both width AND height or manually-added screenshots get squashed.
        const maxW = 520;
        const maxH = 680;
        let dispW = maxW;
        let dispH = Math.max(1, Math.round((frame.height * dispW) / frame.width));
        if (dispH > maxH) {
          dispH = maxH;
          dispW = Math.max(1, Math.round((frame.width * dispH) / frame.height));
        }
        htmlParts.push(
          `<p><img class="frame" width="${dispW}" height="${dispH}" src="cid:${cid}" alt="${esc(block.caption || 'Screenshot')}" /></p>`,
        );
        if (block.caption) htmlParts.push(`<p class="caption">${esc(block.caption)}</p>`);
      }
    }

    if (sectionComments.length) {
      htmlParts.push('<p class="comments-title">COMMENTS</p>');
      for (const c of sectionComments) {
        const quote = (c.quote || '').trim();
        const note = (c.comment || '').trim();
        htmlParts.push('<div class="comment">');
        if (quote) htmlParts.push(`<p class="quote">${esc(`"${quote}"`)}</p>`);
        if (note) htmlParts.push(`<p>${esc(note)}</p>`);
        htmlParts.push('</div>');
      }
    }
  }

  htmlParts.push('</body></html>');
  const mhtml = buildMhtml(sop.title, htmlParts.join('\r\n'), embeddedImages);
  triggerDownload(safeFilename(sop.title), mhtml);
}
